import React from "react";
import { CollapseFAQ, H3, NamedLink } from '../../../components';
import { FormattedMessage } from '../../../util/reactIntl';

import ContinueIcon from '../../TopbarContainer/Topbar/images/continue-icon.svg';
import SydneyOpera from '../images/Australia - Sydney Opera House - White Domes.jpg'
import SydneyBoats from '../images/Australia - Sydney - Boats on Bondi Beach.jpg';

import { australiaFaq } from './faqs';

import css from '../HouseSittingAbroadPage.module.css';

const AustraliaPage = props => {
    return (
        <div className={css.root}>
            <H3>
                House Sitting in Australia
            </H3>
            <p className={css.info}>
                Explore the stunning landscapes, vibrant cities, and unique wildlife of Australia with house sitting. Whether you're in the bustling suburbs of Sydney, the cultural heart of Melbourne, or the serene Outback, HouseSit Match connects you with homeowners seeking responsible caretakers for their properties and pets.
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>

            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={SydneyOpera} alt='Discover distant destinations by housesitting and saving money' />
                <p className={css.info}>
                    Discover distant destinations by housesitting and saving money
                </p>
            </div>

            <p className={css.subtitle}>
                Why Choose HouseSit Match?
            </p>
            <p className={css.info}>
                Choosing HouseSit Match for your house sitting adventure in Australia offers several distinct advantages. Our platform is user-friendly and provides access to a wide range of listings across Australia, making it easy to find a house sit that suits your needs and preferences.
            </p>
            <br />
            <p className={css.info}>
                Additionally, the secure messaging system and supportive community ensure a smooth and enjoyable experience, offering peace of mind for both homeowners and house sitters.
            </p>

            <p className={css.subtitle}>
                The Benefits of House Sitting in Australia
            </p>
            <p className={css.info}>
                House sitting in Australia offers numerous benefits to both homeowners and house sitters, including:
            </p>
            <ul className={css.list}>
                <li>
                    <strong>Free Accommodation: </strong>
                    House sitting allows you to save on lodging expenses while experiencing a variety of Australian lifestyles. Whether you’re looking to stay in the heart of the city or the tranquil countryside, there are opportunities to suit your needs.
                </li>
                <li>
                    <strong>Caring for Pets: </strong>
                    Many house sitting roles involve taking care of pets, which can be a rewarding and enjoyable experience. Building a bond with pets and giving them the care they need adds a personal touch to your travel experience.

                </li>
                <li>
                    <strong>Authentic Experience: </strong>
                    Live like a local in Australia, away from the tourist hubs. House sitting allows you to immerse yourself in the culture, enjoy the daily life of the area, and form connections with locals.
                </li>
                <li>
                    <strong>Flexibility: </strong>
                    You can choose from a range of assignments that fit your travel schedule, whether it's a short-term house sit or a longer stay. This flexibility makes house sitting an excellent choice for solo travelers, couples, or families.
                </li>
                <li>
                    <strong>Cost Savings: </strong>
                    With free accommodation and potentially some utilities provided, house sitting can significantly reduce your travel costs, making it an affordable option for those wanting to explore Australia without breaking the bank.
                </li>
            </ul>

            <p className={css.subtitle}>
                Types of House Sitting Arrangements in Australia
            </p>

            <p className={css.info}>
                House sitting arrangements in Australia vary widely depending on your preferences and requirements, here are some of the options:
            </p>

            <ul className={css.list}>
                <li>
                    <strong>Pet Care: </strong>
                    Commonly includes looking after dogs, cats, or other pets while the homeowners are away.
                </li>
                <li>
                    <strong>Plant Care: </strong>
                    Maintaining indoor and outdoor plants is a typical requirement.
                </li>
                <li>
                    <strong>House Maintenance: </strong>
                    Simple tasks such as watering the garden, collecting mail, and keeping the home secure.
                </li>
                <li>
                    <strong>Farm Sitting: </strong>
                    Caring for livestock, gardening, and general farm duties in rural areas.
                </li>
                <li>
                    <strong>Pool Maintenance: </strong>
                    Looking after swimming pools, which is common in warmer climates.
                </li>
            </ul>

            <div className={css.imageContainerHorizontal}>
                <img className={css.image} src={SydneyBoats} alt="Experience a new climate by housesitting in a new location" />
                <p className={css.info}>
                    Experience a new climate by housesitting in a new location
                </p>
            </div>

            <p className={css.subtitle}>
                Costs of House Sitting in Australia
            </p>
            <p className={css.info}>
                House sitting in Australia can be a budget-friendly way to travel. Generally, house sitting involves free accommodation, which means you save on lodging expenses. Some homeowners might offer a modest allowance for food or minor utilities, but these are not common.
            </p>
            <br />
            <p className={css.info}>
                The primary cost for house sitters is often travel insurance for unexpected situations, but this is a relatively minor expense compared to what you might pay for traditional forms of accommodation when you travel overseas.
            </p>

            <p className={css.subtitle}>
                Legal and Practical Considerations
            </p>

            <p className={css.info}>
                Before embarking on your house sitting adventure in Australia, there are a few legal and practical considerations to be aware of:
            </p>

            <ul className={css.list}>
                <li>
                    <strong>Visas: </strong>
                    Ensure you have the correct visa to stay in Australia. If you're from outside the country or the EU, you may need a tourist visa or a specific type of visa that permits you to stay for longer periods.
                </li>
                <li>
                    <strong>Health Insurance: </strong>
                    It's advisable to have travel insurance that covers medical expenses while you are in Australia.
                </li>
                <li>
                    <strong>Safety and Security: </strong>
                    Always maintain a high level of personal safety awareness. Keep in touch with family or friends back home and have a reliable form of communication (like a local SIM card).
                </li>
                <li>
                    <strong>Local Regulations: </strong>
                    Be aware of local rules regarding pets and property care. Homeowners will typically inform you of these, but being proactive and asking questions is essential.
                </li>
            </ul>

            <p className={css.subtitle}>
                How to Find House Sitting Jobs in Australia
            </p>
            <p className={css.info}>
                Finding house sitting opportunities in Australia involves a straightforward process. Start by signing up on HouseSit Match and completing your profile. Browse available listings to find those that match your interests and availability.
            </p>
            <br />
            <p className={css.info}>
                Reach out to homeowners whose needs align with your skills and preferences, demonstrating why you would be a good fit for their house sitting role. Building a positive reputation through excellent care will increase your chances of securing future opportunities.
            </p>

            <p className={css.subtitle}>
                How to Become a House Sitter in Australia
            </p>
            <p className={css.info}>
                The process of becoming a verified house sitter is made simple with HouseSit Match and its interactive platform:
            </p>
            <ul className={css.list}>
                <li>
                    <strong>Sign Up: </strong>
                    Create a profile on HouseSit Match and complete all the required details.
                </li>
                <li>
                    <strong>Complete Profile: </strong>
                    Add a good quality photo, write a bio that highlights your experience and what makes you a good fit for house sitting roles.
                </li>
                <li>
                    <strong>Apply for Listings: </strong>
                    Browse available listings and apply for those that align with your skills and interests.
                </li>
                <li>
                    <strong>Engage with Homeowners: </strong>
                    Communicate effectively with homeowners to discuss their needs and how you can meet them.
                </li>
                <li>
                    <strong>Build Reputation: </strong>
                    Aim to receive positive feedback for your house sitting experiences, which will help you secure more opportunities in the future.
                </li>
            </ul>

            <H3>
                FAQs from International House Sitters
            </H3>
            <CollapseFAQ
                list={australiaFaq}
            />

            <p className={css.subtitle}>
                See What Our Members Say
            </p>
            <p className={css.info}>
                Include real testimonials from sitters who have successfully completed house sitting assignments in Australia.
            </p>

            <p className={css.subtitle}>
                Embark on Your House Sitting Adventure Today
            </p>
            <p className={css.info}>
                Explore the unique opportunities that house sitting in Australia offers with HouseSit Match. Joining our community gives you access to a world of adventure, pet care, and authentic travel experiences.
            </p>
            <br />
            <p className={css.info}>
                Whether you're a solo traveler, couple, or family, find and secure house sitting assignments that match your interests and schedule.{" "}
                <NamedLink name="SignupPage">Join HouseSit Match today</NamedLink>
                {" "}and start your journey towards unforgettable adventures in Australia!
            </p>

            <NamedLink
                className={css.ctaButton}
                name="SignupPage"
            >
                <FormattedMessage id="HouseSittingAbroadPage.joinNow" />
                <img src={ContinueIcon} className={css.icon} />
            </NamedLink>
        </div>
    )
};

export default AustraliaPage;